/* The plans page — STORY-031.
 *
 * A sales page as much as an account screen, so it renders signed out and from
 * the LOCAL catalogue: seeing prices must not depend on a payment provider
 * being reachable, and it must not depend on having an account.
 *
 * Every figure comes from the catalogue the API serves. A price written into
 * this file would be a second copy, and the copy a customer read would be the
 * one that was wrong.
 *
 * **Usage is the most important thing on the screen**, and it is why this page
 * exists at all rather than a marketing table: a Member who does not know they
 * are one Event from a wall will experience the limit as a bug. The wall is
 * announced before anything refuses.
 */

import type { Plan } from '@eventuley/contracts';
import { formatMoney, money, parseCurrency } from '@eventuley/domain';
import { formatCount, type MessageKey } from '@eventuley/i18n';
import { useSearch } from '@tanstack/react-router';
import { ApiError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Callout, StandaloneHeader } from '../../../shared/ui/index.js';
import { usePlans, useStartCheckout, useUsage } from '../api/index.js';

/* An explicit map, never `t('plans.' + code)`. A constructed key defeats
 * extraction and unused-key detection, which are what keep the catalogue
 * honest — `i18n.md`. */
const PLAN_NAME: Record<string, MessageKey> = {
  free: 'plans.free',
  pro: 'plans.pro',
  business: 'plans.business',
  enterprise: 'plans.enterprise',
};

/** The three meters, paired with the plan field and the usage field each reads. */
const METERS = [
  { key: 'plans.active_events', limitOf: 'activeEventLimit', usedOf: 'activeEvents' },
  { key: 'plans.seats', limitOf: 'seatLimit', usedOf: 'seats' },
  { key: 'plans.contacts', limitOf: 'contactLimit', usedOf: 'contacts' },
] as const satisfies readonly {
  key: MessageKey;
  limitOf: keyof Plan;
  usedOf: 'activeEvents' | 'seats' | 'contacts';
}[];

/* Warns BEFORE the wall as well as at it. STORY-031: a Member who does not
 * know they are one Event from the limit will experience the limit as a bug,
 * and a message that only appears once the action is already refused arrives
 * too late to be that warning.
 *
 * Four fifths is the threshold: on Free — where every limit is 1 — it never
 * fires early, because one of one is AT the limit and gets the stronger line. */
function LimitWarning({ used, limit }: { readonly used: number; readonly limit: number }) {
  const t = useTranslate();

  if (used >= limit) return <span className="plans__at-limit">{t('plans.at_limit')}</span>;
  if (used / limit >= 0.8)
    return <span className="plans__near-limit">{t('plans.near_limit')}</span>;
  return null;
}

function PlanName({ code }: { readonly code: string }) {
  const t = useTranslate();
  const key = PLAN_NAME[code];
  return <>{key ? t(key) : code}</>;
}

/**
 * The purchase action — STORY-032.
 *
 * Absent for Free, which is not something you buy, and for the tier already
 * held. A button that opens a checkout for the plan somebody is already on is
 * an invitation to be charged twice for the same thing.
 *
 * **Every refusal is shown, and nothing is optimistic.** The story asks for
 * "never a spinner and never a silent success", so a failure says plainly that
 * nothing was charged — the one thing a person wants to know when a payment
 * screen fails to open is whether their card was touched.
 */
function ChoosePlan({
  plan,
  workspace,
  isCurrent,
}: {
  readonly plan: Plan;
  readonly workspace: string | undefined;
  readonly isCurrent: boolean;
}) {
  const t = useTranslate();
  const checkout = useStartCheckout(workspace);

  /* Free has no price to charge and the current tier has nothing to change to.
   * The spacer keeps the header cells the same height so the prices in the
   * other columns stay on one line — without it the row of prices steps. */
  if (plan.monthlyPriceMinor === 0 || isCurrent) {
    return <span className="plans__cta-spacer" aria-hidden="true" />;
  }

  /* Signed out this is a sales page, so it says how to proceed rather than
   * offering a button that cannot work. */
  if (workspace === undefined) {
    return <span className="field__hint">{t('plans.sign_in_to_upgrade')}</span>;
  }

  /* `noUncheckedIndexedAccess` is on, so the lookup is genuinely `| undefined`
   * — and the raw code is the right fallback for the same reason `PlanName`
   * gives: naming a future tier after an existing one is worse than showing
   * its code. */
  const nameKey = PLAN_NAME[plan.code];
  const name = nameKey ? t(nameKey) : plan.code;

  const failure =
    checkout.error instanceof ApiError && checkout.error.code === 'forbidden'
      ? t('plans.checkout_forbidden')
      : checkout.isError
        ? t('plans.checkout_failed')
        : null;

  return (
    <>
      <button
        type="button"
        className="btn btn--primary plans__cta"
        disabled={checkout.isPending}
        onClick={() => {
          checkout.mutate(
            { planCode: plan.code, billingPeriod: 'monthly' },
            {
              /* A full navigation, not a router push: the destination is the
               * provider's domain and the router only knows about ours. */
              onSuccess: (result) => {
                window.location.assign(result.redirectUrl);
              },
            },
          );
        }}
      >
        {checkout.isPending ? t('plans.opening_checkout') : t('plans.choose', { plan: name })}
      </button>
      {failure === null ? null : (
        <span className="plans__at-limit" role="alert">
          {failure}
        </span>
      )}
    </>
  );
}

export function PlansScreen() {
  const t = useTranslate();
  const { locale } = useI18n();
  /* The workspace is NAMED by the client and authorised by the server, exactly
   * like any other path or query parameter — ADR-0010. Absent when signed out. */
  const { workspace } = useSearch({ strict: false }) as { workspace?: string };

  const { data: catalogue } = usePlans();
  const { data: usage } = useUsage(workspace);

  if (!catalogue) {
    return (
      <div className="standalone">
        <div className="standalone__inner">
          <StandaloneHeader workspace={workspace} title={t('plans.title')} />
          <p className="field__hint">{t('plans.loading')}</p>
        </div>
      </div>
    );
  }

  /* Formatted through Intl from integer minor units, with the digit count taken
   * from the currency — never a hardcoded symbol and never an assumption of two
   * decimals, since JPY has none and KWD has three. ADR-0005, `i18n.md`. */
  const price = (minor: number, currency: string) => {
    const code = parseCurrency(currency);
    if (!code) return String(minor);
    return formatMoney(money(minor, code), locale);
  };

  /* Through the formatter, never `String(n)`. A four-figure limit reads
   * `1,000` in English and `1.000` in Turkish, and `i18n.md` bans formatting a
   * number by hand for exactly that reason. Found by the pseudo-locale sweep
   * once it started waiting for this screen to finish loading — 2026-08-22. */
  const limitLabel = (limit: number | null) =>
    limit === null ? t('plans.unlimited') : formatCount(limit, { locale });

  return (
    /* Its own container, because this route renders OUTSIDE the workspace shell
     * — it is a sales page that has to work with no session. Without it the
     * content sat flush against the viewport edge and the table stretched to
     * whatever width the window happened to be. */
    <div className="standalone">
      <div className="standalone__inner">
        <StandaloneHeader workspace={workspace} title={t('plans.title')} />
        <p className="field__hint">{t('plans.body')}</p>

        {/* A real table with real headers. A plan comparison built from divs is a
          common screen-reader failure: the reader cannot tell which figure
          belongs to which tier. */}
        <div className="plans__scroll">
          <table className="plans__table">
            <thead>
              <tr>
                <th scope="col">
                  <span className="sr-only">{t('plans.title')}</span>
                </th>
                {catalogue.plans.map((plan) => (
                  <th scope="col" key={plan.code} data-current={usage?.plan === plan.code}>
                    <div className="plans__tier">
                      {/* Name and badge on ONE line. Stacked, the badge pushed
                        the current tier's price a line below every other
                        tier's, so the row of prices stepped at whichever column
                        happened to be current — the one row on the page that
                        most wants to be scannable across. */}
                      <span className="plans__tier-head">
                        <span className="plans__tier-name">
                          {/* The raw code for anything the map does not know. The
                            CHECK constraint bounds codes to these four today, so
                            this is unreachable — but falling back to 'plans.free'
                            would render a future paid tier under the free tier's
                            NAME with paid prices beside it, which is the worst
                            available lie. */}
                          <PlanName code={plan.code} />
                        </span>
                        {usage?.plan === plan.code ? (
                          <span className="badge">{t('plans.current')}</span>
                        ) : null}
                      </span>

                      {/* The price belongs to the tier, not to a body row. As two
                        ordinary rows it carried the same visual weight as the
                        contact limit — the single most important number on a
                        pricing page, indistinguishable from the least. */}
                      <span className="plans__price">
                        <span className="plans__price-amount">
                          {price(plan.monthlyPriceMinor, plan.currency)}
                        </span>
                        <span className="plans__price-period">{t('plans.monthly')}</span>
                      </span>
                      {plan.annualPriceMinor > 0 ? (
                        <span className="plans__price-annual">
                          {t('plans.per_year_billed', {
                            amount: price(plan.annualPriceMinor, plan.currency),
                          })}
                        </span>
                      ) : null}

                      <ChoosePlan
                        plan={plan}
                        workspace={workspace}
                        isCurrent={usage?.plan === plan.code}
                      />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {METERS.map((meter) => (
                <tr key={meter.key}>
                  <th scope="row">{t(meter.key)}</th>
                  {catalogue.plans.map((plan) => {
                    const limit = plan[meter.limitOf] as number | null;
                    const used = usage?.[meter.usedOf];

                    return (
                      <td key={plan.code} data-current={usage?.plan === plan.code}>
                        <span className="plans__limit">{limitLabel(limit)}</span>
                        {/* Usage as TEXT, not only as a bar. A meter drawn with no
                          number is unreadable to a screen reader and to anybody
                          who needs to know how much room is left. */}
                        {used === undefined ? null : (
                          <span className="plans__usage">
                            {limit === null
                              ? t('plans.usage_unlimited', { used: formatCount(used, { locale }) })
                              : t('plans.usage', {
                                  used: formatCount(used, { locale }),
                                  limit: formatCount(limit, { locale }),
                                })}
                          </span>
                        )}
                        {used === undefined || limit === null ? null : (
                          <LimitWarning used={used} limit={limit} />
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Callout tone="info">{t('plans.active_event_meaning')}</Callout>
        <p className="field__hint">{t('plans.teams_need_pro')}</p>
        <p className="field__hint">{t('plans.annual_saving')}</p>
        {/* The jurisdiction is not known until checkout, and saying so beats
            showing a number that turns out to be lower than the bill. */}
        <p className="field__hint">{t('plans.tax_note')}</p>
      </div>
    </div>
  );
}
